// SampleDetail.js

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInbox, faTruck, faTimes, faMapMarkerAlt,
  faPrint, faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import './SampleDetail.css';

const SampleDetail = ({ order, onClose, onSaveChanges }) => {
  // ---- hooks (unconditional) ----
  const [editedOrder, setEditedOrder] = useState(order ? JSON.parse(JSON.stringify(order)) : null);
  const [error, setError] = useState(null);
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [labelMeta, setLabelMeta] = useState(null); // { url, trackingNumber, rmOrderId, generatedAt }

  // ---- consts/helpers (not hooks) ----
  const statusIcons = { 'Order Received': faInbox, 'Sent Cargo': faTruck };

  const renderAddress = (addressString) => {
    if (!addressString || typeof addressString !== 'string') return null;
    try {
      const address = JSON.parse(addressString);
      return (address && typeof address === 'object') ? address : null;
    } catch {
      return null;
    }
  };

  const addressData = editedOrder ? renderAddress(editedOrder.address) : null;

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().slice(0, 16);
  };

  const triggerDownload = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ---- effect: fetch current label meta (guard inside, not by early return) ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!editedOrder?.id) { setLabelMeta(null); return; }
      try {
        const r = await fetch(`https://service9004.szapfs.org/api/orders/${editedOrder.id}/label`);
        if (!r.ok) { if (!cancelled) setLabelMeta(null); return; }
        const d = await r.json();
        if (!cancelled) {
          if (d.exists && d.url) {
            setLabelMeta({
              url: d.url,
              trackingNumber: d.trackingNumber || null,
              rmOrderId: d.royalMailOrderIdentifier || null,
              generatedAt: d.generatedAt || null
            });
          } else {
            setLabelMeta(null);
          }
        }
      } catch {
        if (!cancelled) setLabelMeta(null);
      }
    })();
    return () => { cancelled = true; };
  }, [editedOrder?.id]);

  // ---- event handlers ----
  const handleDateChange = async (stage, dateValue) => {
    setIsSaving(true);
    setError(null);
    const newDate = dateValue ? new Date(dateValue).toISOString() : null;
    const updatedStages = { ...editedOrder.status.stages, [stage]: newDate };

    try {
      const response = await fetch(`https://service9004.szapfs.org/api/orders/${editedOrder.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_received: updatedStages['Order Received'],
          sent_cargo: updatedStages['Sent Cargo'],
          cargo_delivered: updatedStages['Cargo Delivered'],
        }),
      });
      if (!response.ok) throw new Error('Failed to save timeline');

      setEditedOrder(prev => ({ ...prev, status: { ...prev.status, stages: updatedStages } }));
      if (onSaveChanges) onSaveChanges();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrintRequest = async (printType) => {
    setIsPrinting(true);
    setError(null);
    try {
      const response = await fetch('https://service9004.szapfs.org/api/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: editedOrder.id,
          items: editedOrder.items,
          type: printType,
          overwrite: true
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.details || result.error || 'Failed to print labels');

      if (result.labelUrls && result.labelUrls.length > 0) {
        result.labelUrls.forEach(triggerDownload);
        setLabelMeta({
          url: result.labelUrls[0],
          trackingNumber: result.trackingNumber || null,
          rmOrderId: result.royalMailOrderIdentifier || null,
          generatedAt: new Date().toISOString()
        });
      }
      if (onSaveChanges) onSaveChanges();
      setShowPrintDropdown(false);
    } catch (err) {
      setError('Print Error: ' + err.message);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleRedownload = () => {
    if (labelMeta?.url) triggerDownload(labelMeta.url);
  };

  const hasLabel = !!labelMeta?.url;

  // ---- after all hooks: allow early return ----
  if (!editedOrder) return null;

  // ---- UI ----
  const modalContent = (
    <div className="samplemanager-detail-overlay">
      <div className="samplemanager-detail-modal">
        <div className="samplemanager-detail-header">
          <div className="samplemanager-detail-icon">
            <FontAwesomeIcon icon={statusIcons[editedOrder.status.currentStage] || faInbox} />
          </div>
          <div className="samplemanager-detail-title">
            <h3>Order Details</h3>
            <p className="samplemanager-detail-status">{editedOrder.status.currentStage}</p>
          </div>
          <button className="samplemanager-detail-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        <div className="samplemanager-detail-body">
          {error && <div className="samplemanager-detail-error">{error}</div>}

          <div className="samplemanager-detail-content">
            <div className="samplemanager-detail-flex-container">
              {/* LEFT */}
              <div className="samplemanager-detail-left-column">
                <div className="samplemanager-detail-order-id">{editedOrder.id}</div>

                <div className="samplemanager-detail-section">
                  <h4>Customer & Shipping</h4>
                  <div className="samplemanager-detail-grid">
                    <div className="samplemanager-detail-item-container">
                      <div className="samplemanager-detail-item">
                        <div className="samplemanager-detail-item-label">Name</div>
                        <div className="samplemanager-detail-item-address">{editedOrder.firstName} {editedOrder.lastName}</div>
                      </div>
                      <div className="samplemanager-detail-item">
                        <div className="samplemanager-detail-item-label">Email</div>
                        <div className="samplemanager-detail-item-address">{editedOrder.email || 'N/A'}</div>
                      </div>
                    </div>

                    {addressData ? (
                      <>
                        <hr className="samplemanager-detail-address-separator" />
                        <div className="samplemanager-detail-address-grid">
                          <div className="samplemanager-detail-item">
                            <div className="samplemanager-detail-item-label">
                              <FontAwesomeIcon icon={faMapMarkerAlt} /> Recipient
                            </div>
                            <div className="samplemanager-detail-item-address">{addressData.name}</div>
                          </div>
                          {addressData.company && (
                            <div className="samplemanager-detail-item">
                              <div className="samplemanager-detail-item-label">Company</div>
                              <div className="samplemanager-detail-item-address">{addressData.company}</div>
                            </div>
                          )}
                          <div className="samplemanager-detail-item">
                            <div className="samplemanager-detail-item-label">Address</div>
                            <div className="samplemanager-detail-item-address">
                              {addressData.address1}
                              {addressData.address2 && addressData.address2 !== addressData.address1 && (<><br />{addressData.address2}</>)}
                            </div>
                          </div>
                          <div className="samplemanager-detail-item">
                            <div className="samplemanager-detail-item-label">Location</div>
                            <div className="samplemanager-detail-item-address">
                              {[addressData.city, addressData.province || addressData.province_code, addressData.zip].filter(Boolean).join(', ')}
                            </div>
                          </div>
                          <div className="samplemanager-detail-item">
                            <div className="samplemanager-detail-item-label">Country</div>
                            <div className="samplemanager-detail-item-address">{addressData.country}</div>
                          </div>
                          {addressData.phone && (
                            <div className="samplemanager-detail-item">
                              <div className="samplemanager-detail-item-label">Phone</div>
                              <div className="samplemanager-detail-item-address">{addressData.phone}</div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="samplemanager-detail-item">
                        <div className="samplemanager-detail-item-label"><FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address</div>
                        <div className="samplemanager-detail-item-address">No address provided.</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="samplemanager-detail-section">
                  <h4>Kits in this Order</h4>
                  <div className="samplemanager-detail-kit-grid">
                    {editedOrder.items.map(item => (
                      <div key={item.KitID} className="samplemanager-detail-kit-card">
                        <img src={item.ImageURL} alt={item.KitName} className="samplemanager-detail-kit-image" />
                        <div className="samplemanager-detail-kit-name">{item.KitName}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="samplemanager-detail-print-section">
                  <div className="samplemanager-detail-print-buttons">
                    <div className="samplemanager-detail-print-left">
                      <button
                        className="samplemanager-detail-print-all"
                        onClick={() => handlePrintRequest('all')}
                        disabled={isPrinting}
                        title={hasLabel ? 'Create a fresh label and overwrite the current one' : 'Create a shipping label'}
                      >
                        <FontAwesomeIcon icon={faPrint} /> {isPrinting ? 'Printing...' : (hasLabel ? 'Print New Label (overwrite)' : 'Print All')}
                      </button>
                      <div className="samplemanager-detail-print-details">
                        1 Shipping, {editedOrder.items.length} Return Labels
                      </div>

                 
                    </div>

                    <div className="samplemanager-detail-print-right">
                      <div className="samplemanager-detail-dropdown">
                        <button
                          className="samplemanager-detail-dropdown-button"
                          onClick={() => setShowPrintDropdown(!showPrintDropdown)}
                          disabled={isPrinting}
                        >
                          <FontAwesomeIcon icon={faChevronDown} />
                        </button>
                        {showPrintDropdown && (
                          <div className="samplemanager-detail-dropdown-menu">
                            <button onClick={() => handlePrintRequest('shipping')} disabled={isPrinting}>Print Shipping Label</button>
                            <button onClick={() => handlePrintRequest('return')} disabled={isPrinting}>Print Return Labels</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* RIGHT */}
              <div className="samplemanager-detail-right-column">
                <div className="samplemanager-detail-section">
                  <h4>Status Timeline</h4>
                  <div className="samplemanager-detail-timeline">
                    {Object.keys(statusIcons).map((stage, index) => (
                      <div key={stage} className={`samplemanager-detail-timeline-item ${editedOrder.status.stages[stage] ? 'completed' : ''}`}>
                        <div className="samplemanager-detail-timeline-connector">
                          <div className="samplemanager-detail-timeline-dot"></div>
                          {index < Object.keys(statusIcons).length - 1 && <div className="samplemanager-detail-timeline-line"></div>}
                        </div>
                        <div className="samplemanager-detail-timeline-content">
                          <FontAwesomeIcon icon={statusIcons[stage]} className="samplemanager-detail-stage-icon"/>
                          <span className="samplemanager-detail-stage-name">{stage}</span>
                          <div className="samplemanager-detail-stage-date">
                            <input
                              type="datetime-local"
                              value={formatDateForInput(editedOrder.status.stages[stage])}
                              onChange={(e) => handleDateChange(stage, e.target.value)}
                              className="samplemanager-detail-date-input"
                              disabled={isSaving}
                            />
                          </div>
                        </div>
                        
                      </div>
                      
                    ))}
                        

                  </div>
                   {hasLabel && (
  <div className="samplemanager-detail-labelbox">
    <div className="samplemanager-detail-labelbox-left">
      <div className="samplemanager-detail-labelrow">
        <span className="samplemanager-detail-labelkey">RM Order</span>
        <span className="samplemanager-detail-labelval">{labelMeta.rmOrderId || '—'}</span>
      </div>
      <div className="samplemanager-detail-labelrow">
        <span className="samplemanager-detail-labelkey">Tracking</span>
        <span className="samplemanager-detail-labelval">{labelMeta.trackingNumber || '—'}</span>
      </div>
      <div className="samplemanager-detail-labelrow">
        <span className="samplemanager-detail-labelkey">Generated</span>
        <span className="samplemanager-detail-labelval">
          {labelMeta.generatedAt ? new Date(labelMeta.generatedAt).toLocaleString() : '—'}
        </span>
      </div>
    </div>

    <div className="samplemanager-detail-labelbox-right">
      <button
        className="samplemanager-detail-redownload-btn"
        onClick={handleRedownload}
        disabled={isPrinting || !labelMeta?.url}
        title="Download the current saved label PDF"
      >
        <FontAwesomeIcon icon={faPrint} />
        <span>Re-download Label</span>
      </button>
    </div>
  </div>
)}
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SampleDetail;
